import { z } from 'zod';
import { DomainError } from '../errors.js';
import { cashCurrencySchema, cashParser } from './cash.js';
import type { CtmStatus } from './ctm.js';

/**
 * Gemi kasası (ADR 0110 §1): kasayı ofis kurar, gemi yönetir. Kasa para birimi başına bir
 * hesaptır (ADR 0093 §1 "hesap = gemi + para birimi"); ofis hangi birimlerin açık olduğunu
 * değiştirilemez olaylarla (`cash_account_events`: açma / kapama) belirler, bakiye yine yalnız
 * gemideki işlemlerden türer. Kasa kurulmamış gemide (eski gemiler) her birim serbesttir.
 * Kur çevirisi yok: her birim kendi içinde toplanır (§4). Saf modül.
 */

/** Ofis kasayı kurarken önerilen birimler (sahip isteği 2026-10-23). */
export const CASH_DEFAULT_ACCOUNT_CURRENCIES = ['EUR', 'USD', 'TRY', 'GBP'] as const;
export const CASH_ACCOUNT_ACTIONS = ['open', 'close'] as const;
export type CashAccountAction = (typeof CASH_ACCOUNT_ACTIONS)[number];
/** Kapama gerekçesi; CTM fark gerekçesi aynı sınırda. */
export const CASH_ACCOUNT_REASON = { min: 5, max: 500 } as const;
export const CTM_VARIANCE_REASON = { min: 5, max: 500 } as const;
/** Bir gemide en çok bu kadar para birimi kasası. */
export const CASH_ACCOUNT_MAX = 20;

export interface CashAccountEvent {
  id: string;
  currency: string;
  action: CashAccountAction;
  /** Olayın anı (UTC ISO). */
  at: string;
  reason: string | null;
}

export interface CashAccountState {
  currency: string;
  open: boolean;
  /** Son açılış anı. */
  openedAt: string;
  closedAt: string | null;
  /** Kapama gerekçesi. */
  reason: string | null;
}

/** Olaylardan kasa durumu (son olay geçerli); para birimine göre sıralı. */
export function cashAccountStates(events: readonly CashAccountEvent[]): CashAccountState[] {
  const ordered = [...events].sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
  const states = new Map<string, CashAccountState>();
  for (const e of ordered) {
    const previous = states.get(e.currency);
    states.set(
      e.currency,
      e.action === 'open'
        ? { currency: e.currency, open: true, openedAt: e.at, closedAt: null, reason: null }
        : {
            currency: e.currency,
            open: false,
            openedAt: previous?.openedAt ?? e.at,
            closedAt: e.at,
            reason: e.reason,
          },
    );
  }
  return [...states.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/**
 * Ofisin olayı geçerli mi: açık kasa yeniden açılmaz; açık olmayan kapanmaz; bakiyesi ya da
 * yolda parası olan kasa kapanmaz (nakit kayıtsız kalmasın).
 */
export function assertCashAccountEvent(
  states: readonly CashAccountState[],
  input: { currency: string; action: CashAccountAction; balance: number; inTransit: number },
): void {
  const state = states.find((s) => s.currency === input.currency);
  if (input.action === 'open') {
    if (state?.open) throw new DomainError('CASH_ACCOUNT_OPEN', input.currency);
    return;
  }
  if (!state?.open) throw new DomainError('CASH_ACCOUNT_NOT_OPEN', input.currency);
  if (input.balance !== 0 || input.inTransit !== 0) {
    throw new DomainError('CASH_ACCOUNT_NOT_EMPTY', input.currency);
  }
}

/**
 * Gemide yeni giriş ve yeni CTM talebi yalnız açık kasaya (ADR 0110 §1). Kasa kurulmamışsa
 * (hiç olay yok) eski davranış: her birim.
 */
export function assertCashCurrencyAccepted(
  states: readonly CashAccountState[],
  currency: string,
): void {
  if (states.length === 0) return;
  const state = states.find((s) => s.currency === currency);
  if (!state) throw new DomainError('CASH_ACCOUNT_MISSING', currency);
  if (!state.open) throw new DomainError('CASH_ACCOUNT_CLOSED', currency);
}

const setupSchema = z
  .object({
    currencies: z.array(cashCurrencySchema()).min(1, 'REQUIRED').max(CASH_ACCOUNT_MAX, 'TOO_LONG'),
  })
  .superRefine((value, ctx) => {
    if (new Set(value.currencies).size !== value.currencies.length) {
      ctx.addIssue({ code: 'custom', path: ['currencies'], message: 'DUPLICATE_CURRENCY' });
    }
  });
/** Kasa kurulumu ya da birim ekleme: açılacak para birimleri. */
export const parseCashAccountSetup = cashParser(setupSchema);

const closeSchema = z.object({
  currency: cashCurrencySchema(),
  reason: z
    .string()
    .nullish()
    .transform((v) => (v ?? '').normalize('NFC').trim())
    .pipe(
      z
        .string()
        .min(CASH_ACCOUNT_REASON.min, 'REASON_REQUIRED')
        .max(CASH_ACCOUNT_REASON.max, 'TOO_LONG'),
    ),
});
/** Kasa kapama: birim ve gerekçe. */
export const parseCashAccountClose = cashParser(closeSchema);

/* ---------- Yoldaki para ve toplamlar ---------- */

/**
 * Yoldaki para (ADR 0110 §3): ofisin onayladığı ya da gönderdiği, gemide henüz teslim alınmamış
 * CTM; tutar gönderilen (yoksa talep edilen) tutar. Bakiyeye girmez (KRİTİK KURAL, ADR 0093 §3).
 */
export function ctmInTransit(
  items: readonly { currency: string; status: CtmStatus; expectedAmount: number }[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (const item of items) {
    if (item.status !== 'approved' && item.status !== 'transferred') continue;
    out.set(item.currency, (out.get(item.currency) ?? 0) + item.expectedAmount);
  }
  return out;
}

export interface CashPositionRow {
  currency: string;
  /** Ofisin kasası: açık, kapalı ya da kurulmamış (`none`: kasa dışı eski birim). */
  account: 'open' | 'closed' | 'none';
  balance: number;
  inTransit: number;
  /** Teslimden sonra beklenen: bakiye + yolda. */
  expected: number;
}

/**
 * Geminin kasa satırları: açık ve kapalı kasalar, bakiyesi ya da işlemi olan birimler ve yolda
 * parası olan birimler; para birimine göre sıralı.
 */
export function cashPositions(input: {
  accounts: readonly CashAccountState[];
  balances: readonly { currency: string; balance: number }[];
  inTransit: ReadonlyMap<string, number>;
}): CashPositionRow[] {
  const currencies = new Set<string>([
    ...input.accounts.map((a) => a.currency),
    ...input.balances.map((b) => b.currency),
    ...input.inTransit.keys(),
  ]);
  return [...currencies].sort().map((currency) => {
    const state = input.accounts.find((a) => a.currency === currency);
    const balance = input.balances.find((b) => b.currency === currency)?.balance ?? 0;
    const inTransit = input.inTransit.get(currency) ?? 0;
    return {
      currency,
      account: state ? (state.open ? 'open' : 'closed') : 'none',
      balance,
      inTransit,
      expected: balance + inTransit,
    };
  });
}

export interface CashTotalRow {
  currency: string;
  balance: number;
  inTransit: number;
  expected: number;
  /** Bu birimde satırı olan gemi sayısı. */
  vessels: number;
}

/** Filo toplamı: her para birimi kendi içinde; birimler birbirine eklenmez (kur yok, §4). */
export function cashTotals(vessels: readonly (readonly CashPositionRow[])[]): CashTotalRow[] {
  const totals = new Map<string, CashTotalRow>();
  for (const rows of vessels) {
    for (const row of rows) {
      const total = totals.get(row.currency) ?? {
        currency: row.currency,
        balance: 0,
        inTransit: 0,
        expected: 0,
        vessels: 0,
      };
      total.balance += row.balance;
      total.inTransit += row.inTransit;
      total.expected += row.expected;
      total.vessels += 1;
      totals.set(row.currency, total);
    }
  }
  return [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}
